<template>
  <div class="page-container">
    <div class="toolbar">
      <div class="actions">
        <el-button type="primary" @click="openModal">新建粮库</el-button>
        <el-button @click="handleExport">导出Excel</el-button>
        <el-button @click="triggerImport">导入Excel</el-button>
        <input ref="importInput" type="file" accept=".xlsx,.xls" style="display:none" @change="onImportFile" />
      </div>
      <div class="filters">
        <el-input v-model="query.storeName" placeholder="粮库名称" clearable style="width:160px"
                  @keyup.enter="loadGrainStores" />
        <el-input v-model="query.street" placeholder="所属街道" clearable style="width:140px"
                  @keyup.enter="loadGrainStores" />
        <el-select v-model="query.status" placeholder="状态" clearable style="width:120px">
          <el-option label="在用" :value="1" />
          <el-option label="租赁" :value="2" />
          <el-option label="闲置" :value="3" />
        </el-select>
        <el-button type="primary" @click="loadGrainStores">查询</el-button>
        <el-button @click="resetQuery">重置</el-button>
      </div>
    </div>
    <el-table stripe :data="grainStores" border>
      <el-table-column type="index" label="序号" width="60" align="center" />
      <el-table-column prop="storeName" label="粮库名称" min-width="120" />
      <el-table-column prop="address" label="地址" min-width="120" />
      <el-table-column prop="street" label="所属街道" min-width="100" />
      <el-table-column prop="area" label="面积(亩)" width="90" />
      <el-table-column prop="manager" label="负责人" width="100" />
      <el-table-column prop="phone" label="联系电话" width="120" />
      <el-table-column prop="status" label="状态" width="90">
        <template #default="scope">
          <el-tag :type="statusTag(scope.row.status)">{{ statusText(scope.row.status) }}</el-tag>
        </template>
      </el-table-column>
      <el-table-column label="坐标" width="160">
        <template #default="scope">
          <span v-if="scope.row.longitude != null && scope.row.latitude != null" class="coord-cell">
            {{ (+scope.row.longitude).toFixed(4) }}, {{ (+scope.row.latitude).toFixed(4) }}
          </span>
          <span v-else class="coord-empty">未标注</span>
        </template>
      </el-table-column>
      <el-table-column label="操作" width="260" fixed="right">
        <template #default="scope">
          <div style="white-space: nowrap;">
            <el-button size="small" @click="editGrainStore(scope.row)">编辑</el-button>
            <el-button size="small" type="success" plain @click="openManage(scope.row, 'warehouse')">仓房</el-button>
            <el-button size="small" type="warning" plain @click="openManage(scope.row, 'monitor')">监控</el-button>
            <el-button size="small" type="danger" @click="handleDeleteGrainStore(scope.row.id)">删除</el-button>
          </div>
        </template>
      </el-table-column>
    </el-table>

    <el-dialog title="粮库信息" v-model="dialogVisible" width="620px">
      <el-form :model="formData" label-width="100px">
        <el-form-item label="粮库名称">
          <el-input v-model="formData.storeName" />
        </el-form-item>
        <el-form-item label="地址">
          <el-input v-model="formData.address" />
        </el-form-item>
        <el-form-item label="所属街道">
          <el-input v-model="formData.street" placeholder="如：XX街道" />
        </el-form-item>
        <el-form-item label="面积(亩)">
          <el-input v-model="formData.area" type="number" />
        </el-form-item>
        <el-form-item label="负责人">
          <el-select v-model="formData.managerId" filterable placeholder="选择系统用户" style="width:100%" @change="onManagerChange">
            <el-option
              v-for="u in users"
              :key="u.id"
              :label="(u.realName || u.username) + (u.phone ? '（' + u.phone + '）' : '')"
              :value="u.id" />
          </el-select>
        </el-form-item>
        <el-form-item label="联系电话">
          <el-input v-model="formData.phone" readonly placeholder="随负责人自动带出" />
        </el-form-item>
        <el-form-item label="状态">
          <el-select v-model="formData.status" style="width:100%">
            <el-option label="在用" :value="1" />
            <el-option label="租赁" :value="2" />
            <el-option label="闲置" :value="3" />
          </el-select>
        </el-form-item>
        <el-form-item label="地图坐标">
          <MapPicker v-model:lng="formData.longitude" v-model:lat="formData.latitude" />
        </el-form-item>
        <el-form-item label="平面图">
          <el-upload
            class="plan-upload"
            :show-file-list="false"
            :before-upload="beforePlanUpload"
            :http-request="handlePlanUpload"
            accept="image/*">
            <img v-if="formData.planImage" :src="formData.planImage" class="plan-preview" />
            <el-button v-else size="small" type="primary">点击上传平面图</el-button>
          </el-upload>
          <div v-if="formData.planImage" class="plan-tip">
            <el-button size="small" text type="danger" @click="formData.planImage=''">移除</el-button>
            <span>上传后可在「监控管理」中点击平面图标注摄像机位置</span>
          </div>
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="dialogVisible = false">取消</el-button>
        <el-button type="primary" @click="handleSaveGrainStore">确定</el-button>
      </template>
    </el-dialog>

    <!-- 仓房/监控 管理：覆盖编辑区域(右侧内容区)的弹出层，不含左侧菜单与顶部 -->
    <transition name="manage-fade">
      <div v-if="manageVisible" class="manage-mask">
        <div class="manage-panel">
          <div class="manage-title">
            <span>粮库管理 · {{ manageStoreName || '' }}</span>
            <el-button text circle @click="manageVisible = false">
              <el-icon :size="18"><Close /></el-icon>
            </el-button>
          </div>
          <el-tabs v-model="manageTab" class="manage-tabs">
            <el-tab-pane label="仓房管理" name="warehouse">
              <Warehouse :grain-store-id="manageStoreId" :store-name="manageStoreName" :embedded="true" />
            </el-tab-pane>
            <el-tab-pane label="监控平面标注" name="monitor">
              <MonitorPlan :grain-store-id="manageStoreId" :store-name="manageStoreName" :embedded="true" />
            </el-tab-pane>
          </el-tabs>
        </div>
      </div>
    </transition>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted, watch, h } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import { Close } from '@element-plus/icons-vue'
import { getGrainStoreList, saveGrainStore, deleteGrainStore, getUserList, uploadFile,
  exportGrainStores, importGrainStores, downloadGrainStoreTemplate } from '../api'
import Warehouse from './Warehouse.vue'
import MonitorPlan from './MonitorPlan.vue'
import MapPicker from '../components/MapPicker.vue'

const grainStores = ref([])
const users = ref([])
const dialogVisible = ref(false)
const formData = reactive({
  id: null,
  storeName: '',
  address: '',
  street: '',
  area: 0,
  managerId: null,
  manager: '',
  phone: '',
  status: 1,
  longitude: null,
  latitude: null,
  planImage: ''
})

// 查询条件：粮库名称(模糊)、所属街道、状态
const query = reactive({ storeName: '', street: '', status: null })

onMounted(() => {
  loadGrainStores()
  loadUsers()
})

const statusText = (s) => s === 1 ? '在用' : s === 2 ? '租赁' : '闲置'
const statusTag = (s) => s === 1 ? 'success' : s === 2 ? 'warning' : 'info'

const loadGrainStores = async () => {
  try {
    const res = await getGrainStoreList({
      storeName: query.storeName || undefined,
      street: query.street || undefined,
      status: query.status != null ? query.status : undefined
    })
    grainStores.value = res.data
  } catch (error) {
    ElMessage.error('加载粮库失败')
  }
}

const resetQuery = () => {
  query.storeName = ''
  query.street = ''
  query.status = null
  loadGrainStores()
}

const loadUsers = async () => {
  try {
    const res = await getUserList()
    users.value = res.data || []
  } catch (error) {
    console.error('加载用户失败', error)
  }
}

const onManagerChange = (id) => {
  const u = users.value.find(x => x.id === id)
  if (u) {
    formData.manager = u.realName || u.username
    formData.phone = u.phone || ''
  }
}

const openModal = () => {
  Object.assign(formData, { id: null, storeName: '', address: '', street: '', area: 0, managerId: null, manager: '', phone: '', status: 1, longitude: null, latitude: null, planImage: '' })
  dialogVisible.value = true
}

const editGrainStore = (row) => {
  Object.assign(formData, JSON.parse(JSON.stringify(row)))
  if (formData.status === 0) formData.status = 3
  if (formData.longitude === undefined) formData.longitude = null
  if (formData.latitude === undefined) formData.latitude = null
  dialogVisible.value = true
}

const beforePlanUpload = (file) => {
  const isImg = file.type.startsWith('image/')
  if (!isImg) ElMessage.error('请上传图片文件')
  const under10M = file.size / 1024 / 1024 < 10
  if (!under10M) ElMessage.error('图片不能超过 10MB')
  return isImg && under10M
}

const handlePlanUpload = async ({ file }) => {
  try {
    const res = await uploadFile(file, 'plan')
    formData.planImage = res.data
    ElMessage.success('平面图上传成功')
  } catch (error) {
    ElMessage.error('平面图上传失败')
  }
}

const handleSaveGrainStore = async () => {
  try {
    await saveGrainStore(formData)
    ElMessage.success('保存成功')
    dialogVisible.value = false
    loadGrainStores()
  } catch (error) {
    ElMessage.error('保存失败')
  }
}

const handleDeleteGrainStore = async (id) => {
  try {
    await deleteGrainStore(id)
    ElMessage.success('删除成功')
    loadGrainStores()
  } catch (error) {
    ElMessage.error('删除失败')
  }
}

// ============ Excel 导入导出 ============
const importInput = ref(null)

const handleExport = async () => {
  try {
    ElMessage.info('正在生成 Excel...')
    const resp = await exportGrainStores({
      storeName: query.storeName || undefined,
      street: query.street || undefined,
      status: query.status != null ? query.status : undefined
    })
    const blob = new Blob([resp.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    const cd = resp.headers['content-disposition'] || ''
    const match = cd.match(/filename\*=UTF-8''([^;]+)/i) || cd.match(/filename="?([^"]+)"?/)
    a.download = match ? decodeURIComponent(match[1]) : '粮库列表.xlsx'
    a.click()
    URL.revokeObjectURL(url)
    ElMessage.success('导出成功')
  } catch (e) {
    ElMessage.error('导出失败')
  }
}

const triggerImport = async () => {
  // 弹窗里给"下载模板"链接 + "选择文件"按钮
  ElMessageBox({
    title: '导入 Excel',
    message: h('div', [
      h('div', { style: 'margin-bottom:8px;color:#606266' }, '导入将添加新粮库，同名粮库自动跳过。'),
      h('a', {
        href: '#',
        style: 'color:#409eff;text-decoration:underline;',
        onClick: (ev) => { ev.preventDefault(); handleDownloadTemplate() }
      }, '📥 点击下载导入模板 Excel')
    ]),
    confirmButtonText: '选择文件',
    cancelButtonText: '取消',
    showCancelButton: true
  }).then(() => {
    importInput.value?.click()
  }).catch(() => {})
}

const handleDownloadTemplate = async () => {
  try {
    const resp = await downloadGrainStoreTemplate()
    const blob = new Blob([resp.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = '粮库导入模板.xlsx'
    a.click()
    URL.revokeObjectURL(url)
  } catch (e) {
    ElMessage.error('模板下载失败')
  }
}

const onImportFile = async (e) => {
  const file = e.target.files?.[0]
  if (!file) return
  importInput.value.value = '' // 允许重新选同一个文件
  try {
    const res = await importGrainStores(file)
    const d = res.data || {}
    let msg = `导入完成：新增 ${d.ok || 0} 条`
    if (d.skip) msg += `，跳过 ${d.skip} 条（可能重名）`
    ElMessage.success(msg)
    loadGrainStores()
    if (d.errors && d.errors.length > 0) {
      ElMessage.warning('导入细节:\n' + d.errors.join('\n'))
    }
  } catch (e) {
    ElMessage.error('导入失败：' + (e.message || e))
  }
}

// 仓房/监控管理：在粮库管理页内以全屏弹出层管理
const manageVisible = ref(false)
const manageTab = ref('warehouse')
const manageStoreId = ref(null)
const manageStoreName = ref('')

// 弹出时锁定页面滚动
watch(manageVisible, (v) => {
  const content = document.querySelector('.content')
  if (!content) return
  if (v) { content.style.overflow = 'hidden' } else { content.style.overflow = '' }
})

const openManage = (row, tab) => {
  manageStoreId.value = row.id
  manageStoreName.value = row.storeName
  manageTab.value = tab
  manageVisible.value = true
}
</script>

<style scoped>
.coord-cell { font-size: 12px; color: #303133; }
.coord-empty { font-size: 12px; color: #c0c4cc; }
.toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
  flex-wrap: wrap;
}
.actions,
.filters {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}
.plan-preview {
  max-width: 100%;
  max-height: 220px;
  border: 1px dashed #dcdfe6;
  border-radius: 4px;
  display: block;
}
.plan-tip {
  margin-top: 8px;
  font-size: 12px;
  color: #909399;
}
.plan-tip span {
  margin-left: 8px;
}
/* 管理弹出层：覆盖编辑区(右侧内容区)，不含左侧菜单与顶部 */
.manage-mask {
  position: fixed;
  left: 240px;
  top: 84px;
  right: 20px;
  bottom: 20px;
  background: rgba(0, 0, 0, 0.45);
  z-index: 999;
}
.manage-panel {
  position: absolute;
  inset: 0;
  background: #fff;
  display: flex;
  flex-direction: column;
  border-radius: 6px;
  overflow: hidden;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.12);
}
.manage-title {
  height: 48px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 16px;
  font-weight: 600;
  border-bottom: 1px solid #ebeef5;
}
.manage-tabs {
  flex: 1;
  display: flex;
  flex-direction: column;
  height: auto;
}
.manage-tabs :deep(.el-tabs__header) {
  margin-bottom: 0;
  padding-left: 12px;
}
.manage-tabs :deep(.el-tabs__content) {
  flex: 1;
  overflow: auto;
  padding: 16px;
}
.manage-tabs :deep(.el-tab-pane) {
  height: 100%;
}
.manage-fade-enter-active,
.manage-fade-leave-active {
  transition: opacity 0.2s ease;
}
.manage-fade-enter-from,
.manage-fade-leave-to {
  opacity: 0;
}
</style>
